import React, { useEffect, useState, useCallback } from 'react';
import {
  Button, Table, Badge, Spinner, Form, InputGroup, Row, Col, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import _ from 'lodash';
import dayjs from 'dayjs';
import Error from '../../Components/Error';
import ServiceOfferInputModal from './Components/ServiceOfferInputModal';
import PdfPreviewModal from './Components/PdfPreviewModal';
import useModalState from '../../Hooks/useModalState';
import ServiceOfferService from '../../Services/ServiceOfferService';
import UserService from '../../Services/UserService';
import { useIngestController } from '../../Components/IngestWorkflow/IngestController';

// Available status options
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'lead', label: 'Lead' },
  { value: 'quoting', label: 'Quoting' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'expired', label: 'Expired' },
  { value: 'rejected', label: 'Rejected' },
];

// Default pagination settings
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;

// Selectable page sizes. MAX_LIMIT must not exceed the API's own cap (ServiceOffersController
// clamps `limit` to 100) — anything larger would silently be reduced server-side and the
// "items per page" selector would then disagree with what actually comes back.
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const MAX_LIMIT = Math.max(...PAGE_SIZE_OPTIONS);

// Statuses that should NOT show expiration warnings
const EXCLUDED_WARNING_STATUSES = ['accepted', 'rejected', 'expired'];

// Threshold for "expiring soon" warning (in days)
const EXPIRATION_WARNING_DAYS = 3;

/**
 * Calculate days until expiration
 * @param {string} validUntilDate - ISO date string
 * @returns {number} - Days until expiration (negative if expired)
 */
function getDaysUntilExpiration(validUntilDate) {
  if (!validUntilDate) return null;
  const validUntil = dayjs(validUntilDate).endOf('day');
  const now = dayjs().startOf('day');
  return validUntil.diff(now, 'day');
}

/**
 * Get expiration warning info for an offer
 * @param {Object} offer - Service offer object
 * @returns {Object|null} - Warning info or null if no warning needed
 */
function getExpirationWarning(offer) {
  if (!offer.validUntil) return null;

  // Don't show warning for excluded statuses
  if (EXCLUDED_WARNING_STATUSES.includes(offer.status?.toLowerCase())) {
    return null;
  }

  const daysUntil = getDaysUntilExpiration(offer.validUntil);

  // No warning if more than 3 days away
  if (daysUntil > EXPIRATION_WARNING_DAYS) {
    return null;
  }

  if (daysUntil < 0) {
    // Already expired
    return {
      type: 'expired',
      color: '#dc3545', // Red
      icon: 'fa-exclamation-circle',
      tooltip: `Expired ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} ago`,
    };
  }

  // Expiring soon (0-3 days)
  return {
    type: 'expiring',
    color: '#ffc107', // Yellow/Amber
    icon: 'fa-exclamation-triangle',
    tooltip: daysUntil === 0
      ? 'Expires today'
      : `Expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
  };
}

/**
 * Component to render expiration warning icon with tooltip
 */
function ExpirationWarning({ offer }) {
  const warning = getExpirationWarning(offer);

  if (!warning) return null;

  const tooltipId = `expiration-tooltip-${offer.Id}`;

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={tooltipId}>{warning.tooltip}</Tooltip>}
    >
      <i
        className={`fa ${warning.icon} ms-2`}
        style={{
          color: warning.color,
          fontSize: '0.9em',
          cursor: 'help',
        }}
        aria-label={warning.tooltip}
      />
    </OverlayTrigger>
  );
}

function ServiceOffers() {
  const { startIngest } = useIngestController();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [myOffersOnly, setMyOffersOnly] = useState(false);

  // Pagination state
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const inputModal = useModalState();
  const previewModal = useModalState();

  // Build query parameters
  const buildQueryParams = useCallback(() => {
    const params = {};

    // Always send pagination. Omitting these used to fall through to the API's legacy branch,
    // which returned every offer in one ~1MB payload and reported totalPages = 1, so the
    // pagination controls below never rendered on the default view.
    params.page = page;
    params.limit = Math.min(limit, MAX_LIMIT);

    // Add search
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    // Add status filter (can be multiple comma-separated)
    if (statusFilter) {
      params.status = statusFilter;
    }

    // Add date filters
    if (dateFrom) {
      params.dateFrom = dayjs(dateFrom).startOf('day').toISOString();
    }
    if (dateTo) {
      params.dateTo = dayjs(dateTo).endOf('day').toISOString();
    }

    // My offers filter — send both email and name for backward compat
    if (myOffersOnly) {
      const userService = UserService.getInstance();
      const email = userService.getEmail();
      const fullName = userService.getFullName();
      if (email) params.createdBy = email;
      if (fullName) params.createdByName = fullName;
    }

    return params;
  }, [page, limit, searchTerm, statusFilter, dateFrom, dateTo, myOffersOnly]);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      const response = await ServiceOfferService.fetchAll(params);

      // Handle API response - unwrap success response if present
      let responseData = response?.data;

      // Unwrap { success: true, data: ... } response format from SuccessResponseMiddleware
      if (responseData && responseData.success === true && responseData.data !== undefined) {
        responseData = responseData.data;
      }

      if (responseData?.items && Array.isArray(responseData.items)) {
        // New paginated format: { items, total, page, limit, totalPages }
        setOffers(responseData.items);
        setTotal(responseData.total || 0);
        setTotalPages(responseData.totalPages || 1);
        setPage(responseData.page || 1);
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        // Alternative format: { data: [...], pagination: {...} }
        setOffers(responseData.data);
        if (responseData.pagination) {
          setTotal(responseData.pagination.total || 0);
          setTotalPages(responseData.pagination.totalPages || 1);
          setPage(responseData.pagination.page || 1);
        }
      } else if (Array.isArray(responseData)) {
        // Legacy format - direct array response
        setOffers(responseData);
        setTotal(responseData.length || 0);
        setTotalPages(1);
      } else {
        // Fallback - treat as empty
        setOffers([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load service offers');
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = _.debounce(() => {
    fetchOffers();
  }, 500);

  // Fetch when filters or pagination change
  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [buildQueryParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, dateFrom, dateTo, limit, myOffersOnly]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'status') {
      setStatusFilter(value);
    } else if (name === 'dateFrom') {
      setDateFrom(value);
    } else if (name === 'dateTo') {
      setDateTo(value);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setMyOffersOnly(false);
    setPage(1);
  };

  const hasActiveFilters = searchTerm || statusFilter || dateFrom || dateTo || myOffersOnly;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  const handleLimitChange = (e) => {
    const nextLimit = parseInt(e.target.value, 10);
    setLimit(Math.min(Number.isNaN(nextLimit) ? DEFAULT_LIMIT : nextLimit, MAX_LIMIT));
    setPage(1);
  };

  const handleDelete = async (offer) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to delete "${offer.title}"?`)) {
      return;
    }

    try {
      await ServiceOfferService.delete(offer.Id);
      fetchOffers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete service offer');
    }
  };

  const handleDuplicate = async (offer) => {
    try {
      await ServiceOfferService.duplicate(offer.Id);
      fetchOffers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to duplicate service offer');
    }
  };

  const handleUpdateStatus = async (offer, status) => {
    try {
      await ServiceOfferService.updateStatus(offer.Id, status);
      fetchOffers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDownloadPdf = async (offer) => {
    setDownloadingId(offer.Id);
    try {
      const response = await ServiceOfferService.generatePdf(offer.Id);
      const pdfData = response?.data?.data;
      ServiceOfferService.downloadPdf(pdfData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      lead: { bg: 'warning', text: 'dark', label: 'Lead' },
      quoting: { bg: 'info', label: 'Quoting' },
      draft: { bg: 'secondary', label: 'Draft' },
      sent: { bg: 'primary', label: 'Sent' },
      accepted: { bg: 'success', label: 'Accepted' },
      expired: { bg: 'danger', label: 'Expired' },
      rejected: { bg: 'dark', label: 'Rejected' },
    };
    const config = statusMap[status] || statusMap.draft;
    return <Badge bg={config.bg} text={config.text}>{config.label}</Badge>;
  };

  // Calculate pagination info
  const startItem = total > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, total);

  // Get results count text
  const getResultsText = () => {
    if (loading) return 'Loading...';
    if (total > 0) return `Showing ${startItem}-${endItem} of ${total}`;
    return 'No results found';
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h3 className="fw-bold mb-1">
            <i className="fa fa-file-invoice me-2 text-primary" />
            Service Offers
          </h3>
          <small className="text-muted">
            Manage service offers and operator leads
          </small>
        </div>
        <div className="d-flex gap-2">
          <Button
            onClick={() => inputModal.show({})}
            variant="primary"
          >
            <i className="fa fa-plus me-1" />
            New Offer
          </Button>
          <Button
            onClick={() => inputModal.show({ leadMode: 'lead' })}
            variant="outline-primary"
          >
            <i className="fa fa-bullhorn me-1" />
            New Lead
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <InputGroup>
          <span className="input-group-text">
            <i className="fa fa-search" />
          </span>
          <Form.Control
            type="text"
            placeholder="Search by title, recipient, email, or reference (e.g. SO-ABC123)..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <Button variant="outline-secondary" onClick={clearSearch}>
              <i className="fa fa-times" />
            </Button>
          )}
          <Button
            variant={myOffersOnly ? 'secondary' : 'outline-secondary'}
            onClick={() => setMyOffersOnly(!myOffersOnly)}
            title="Show only my offers"
          >
            <i className="fa fa-user me-1" />
            My Offers
          </Button>
          <Button
            variant={showFilters ? 'secondary' : 'outline-secondary'}
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="fa fa-filter me-1" />
            Filters
            {hasActiveFilters && <span className="badge bg-danger ms-1">!</span>}
          </Button>
        </InputGroup>
        {searchTerm && (
          <small className="text-muted ms-1">
            <i className="fa fa-search me-1" />
            Searching: &quot;
            {searchTerm}
            &quot;
          </small>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="card mb-3">
          <div className="card-body">
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={statusFilter}
                    onChange={handleFilterChange}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Date From</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateFrom"
                    value={dateFrom}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Date To</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateTo"
                    value={dateTo}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Button
                  variant="outline-danger"
                  onClick={clearFilters}
                  className="mb-3"
                >
                  <i className="fa fa-times me-1" />
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <small className="text-muted">
          {getResultsText()}
          {hasActiveFilters && ' (filtered)'}
        </small>
        {hasActiveFilters && !loading && (
          <Button
            variant="link"
            size="sm"
            onClick={clearFilters}
            className="p-0"
          >
            <i className="fa fa-times-circle me-1" />
            Clear all filters
          </Button>
        )}
      </div>

      <Error error={error} setError={setError} />

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading service offers...</p>
        </div>
      ) : (
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>Title</th>
              <th>Recipient</th>
              <th>Created By</th>
              <th>Date</th>
              <th>Valid Until</th>
              <th>Tables</th>
              <th>Status</th>
              <th style={{ width: '250px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  <i className="fa fa-file-invoice fa-2x mb-2 d-block opacity-50" />
                  No service offers found.
                  {hasActiveFilters && ' Try adjusting your filters.'}
                </td>
              </tr>
            ) : (
              offers.map((offer) => (
                <tr key={offer.Id}>
                  <td>
                    <strong>{offer.title}</strong>
                  </td>
                  <td>
                    {offer.recipient?.name}
                    {offer.recipient?.email && (
                      <small className="text-muted d-block">
                        {offer.recipient.email}
                      </small>
                    )}
                  </td>
                  <td><small>{offer.creator?.name || '\u2014'}</small></td>
                  <td><small>{formatDate(offer.offerDate)}</small></td>
                  <td>
                    <small>
                      {formatDate(offer.validUntil)}
                      <ExpirationWarning offer={offer} />
                    </small>
                  </td>
                  <td>
                    <Badge bg="light" text="dark">
                      {offer.serviceTables?.length || 0}
                    </Badge>
                  </td>
                  <td>{getStatusBadge(offer.status)}</td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => inputModal.show(offer)}
                        title="Edit"
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      {offer.status === 'accepted' && (
                        <OverlayTrigger
                          overlay={<Tooltip>Build bookings from this offer</Tooltip>}
                        >
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => startIngest(offer.Id)}
                          >
                            <i className="fa fa-magic" />
                          </Button>
                        </OverlayTrigger>
                      )}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleDuplicate(offer)}
                        title="Duplicate"
                      >
                        <i className="fa fa-copy" />
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleDownloadPdf(offer)}
                        disabled={downloadingId === offer.Id}
                        title="Download PDF"
                      >
                        {downloadingId === offer.Id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="fa fa-file-pdf" />
                        )}
                      </Button>
                      {!EXCLUDED_WARNING_STATUSES.includes(offer.status)
                        && offer.validUntil && getDaysUntilExpiration(offer.validUntil) < 0 && (
                        <>
                          <OverlayTrigger overlay={<Tooltip>Mark as Expired</Tooltip>}>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleUpdateStatus(offer, 'expired')}
                            >
                              <i className="fa fa-clock" />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>Mark as Rejected</Tooltip>}>
                            <Button
                              size="sm"
                              variant="outline-dark"
                              onClick={() => handleUpdateStatus(offer, 'rejected')}
                            >
                              <i className="fa fa-times-circle" />
                            </Button>
                          </OverlayTrigger>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(offer)}
                        title="Delete"
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Pagination Controls — shown whenever there are results, so the page-size selector
          stays reachable even when everything fits on a single page. */}
      {!loading && total > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <i className="fa fa-chevron-left" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              <i className="fa fa-chevron-right" />
            </Button>

            <Form.Select
              size="sm"
              value={page}
              onChange={(e) => handlePageChange(parseInt(e.target.value, 10))}
              style={{ width: 'auto' }}
            >
              {Array.from({ length: totalPages }, (unused, idx) => {
                const pageNum = idx + 1;
                return (
                  <option key={pageNum} value={pageNum}>
                    Page
                    {' '}
                    {pageNum}
                  </option>
                );
              })}
            </Form.Select>

            <span className="text-muted small">
              of
              {' '}
              {totalPages}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 small text-muted">Items per page:</Form.Label>
            <Form.Select
              size="sm"
              value={limit}
              onChange={handleLimitChange}
              style={{ width: 'auto' }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>
      )}

      {inputModal.isVisible && (
        <ServiceOfferInputModal
          show={inputModal.isVisible}
          onHide={inputModal.hide}
          offer={inputModal.selectedObject}
          onSuccess={fetchOffers}
          initialMode={inputModal.selectedObject?.leadMode}
        />
      )}

      {previewModal.isVisible && (
        <PdfPreviewModal
          show={previewModal.isVisible}
          onHide={previewModal.hide}
          pdfBase64={previewModal.selectedObject?.pdf}
          filename={previewModal.selectedObject?.filename}
        />
      )}
    </>
  );
}

export default ServiceOffers;
